export type CardType = 'POKEMON' | 'ONE_PIECE' | 'DIGIMON' | 'HOLOLIVE' | 'WEISS_SCHWARZ' | 'COOKIERUN' | 'OTHER';

export type PriceChangeStatus =
  | 'INCREASED'
  | 'UNCHANGED'
  | 'SLIGHT_DROP'
  | 'MODERATE_DROP'
  | 'SEVERE_DROP';

/**
 * 타 샵 시세 대조 분류(백엔드 ClassifyDropsJob) — priceChangeStatus(하락 "폭")와 다른 축.
 * "추천가가 오등록일 가능성"을 본다.
 * - IN_RANGE     시세 범위 내(안전) — 일괄 적용 대상
 * - SUSPECT      확인 필요(오등록 의심) — 한 건씩 검토
 * - NO_REFERENCE 참조 시세 없음 — 검증 불가라 가격 조회의 ③ 스토어 반영에서 제외(2026-10-21)
 */
export type DropBucket = 'IN_RANGE' | 'SUSPECT' | 'NO_REFERENCE';

/** SUSPECT 카드의 오등록 의심 강도(정렬·배지용). */
export type DropSeverity = 'SEVERE' | 'MODERATE' | 'MINOR';

/** 서버 검토 결정(백엔드 PR #79) — KEEP = 네이버 가격 유지, APPLY = 수정가 적용(③ 이 보낸다). */
export type PriceReviewDecision = 'KEEP' | 'APPLY';

export interface Card {
  id:                number;
  productName:       string;
  productNumber:     string;
  sellerProductCode: string;
  // Naver 판매자상품코드 중복 그룹을 로컬 임시 코드("-DUP{채널상품번호}")로 구분해 등록한
  // 카드인가(2026-08-20, ImportProductsJob) — 화면 필터에는 안 쓰인다(아래
  // sellerCodeNeedsReview가 그 역할), 진단용으로만 노출된다.
  sellerCodeAmbiguous: boolean;
  // 자동으로 붙은 임시 코드를 직원이 아직 검토하지 않았는가(2026-08-20) — true면 "코드
  // 확인 필요" 필터 대상. ImportProductsJob이 중복 코드를 자동으로 임시 처리·Naver 반영까지
  // 마쳐도(sellerCodeAmbiguous=false가 되어도) 이 값은 직원이 검토를 마칠 때까지 true로 남는다.
  sellerCodeNeedsReview: boolean;
  cardType:          CardType;
  rarity:            string | null;
  expansionCode:     string | null;
  expansionName:     string | null;
  currentPrice:      number | null;
  suggestedPrice:    number | null;
  previousPrice:     number | null;
  priceChangeStatus: PriceChangeStatus;
  priceChangeRate:   number | null;
  stockQuantity:     number | null;
  priceUpdatedAt:    string | null;
  createdAt:         string;
  imageUrl:          string | null;
  isManual:          boolean;
  naverSynced:       boolean | null;
  naverStatus:       string | null;
  naverError:        string | null;
  naverSaleStatus:   string | null;
  naverSyncedAt:     string | null;
  stockSyncedAt:     string | null;

  // 타 샵 시세 대조 분류(ClassifyDropsJob) — 하락 카드에만 채워진다. dropSeverity/
  // dropReason은 dropBucket === 'SUSPECT'일 때만. marketMin/Max/ShopCount는 대조에 쓴 타 샵 시세.
  dropBucket:        DropBucket | null;
  dropSeverity:      DropSeverity | null;
  dropReason:        string | null;
  marketMin:         number | null;
  marketMax:         number | null;
  marketShopCount:   number | null;
  marketCheckedAt:   string | null;
  /**
   * 대조에 쓴 PriceHub 시세의 수집 시각 · 7일 넘어 근거에서 뺐는가(백엔드 PR #79 Q5 · M3, 감사 card-controltower#566).
   * marketShopCount 는 이제 몰 수(같은 몰 여러 상품 = 1곳). 옛 서버는 없음.
   */
  marketCollectedAt?: string | null;
  marketStale?:       boolean;
  /**
   * 서버 검토(백엔드 PR #79 Q3) — 지금 수정가에 유효한 결정과 검토자 · 시각. 없으면 null(검토 대기).
   * ③ 은 '확인 필요' 하락 · 상승 이상치를 APPLY 없이는 건너뛴다. 옛 서버는 없음.
   */
  priceReview?:       PriceReviewDecision | null;
  priceReviewedAt?:   string | null;
  priceReviewedBy?:   string | null;

  // PriceHub 매장별 판매가(2026-10-21, 백엔드 Flyway store V4) — 옛 서버 응답에는 없어서 선택 칸.
  // hubPrice = PriceHub 가 준 이 매장 판매가(수정가가 수동가로 덮여도 남음), hubPriceAt = 그 값이
  // 바뀐 시각, manualOverrideAt = 매장 관리자 네이버 직접 변경 감지 시각(확정 · 무시 전까지 반영 제외).
  hubPrice?:         number | null;
  hubPriceAt?:       string | null;
  manualOverrideAt?: string | null;
  /**
   * 사람이 정한 가격 보호 중(백엔드 PR #77 Q2 — '유지' · ② 적용가 · 판매가 저장 때의 PriceHub 값이 그대로면 가격 받기가 덮지 않는다).
   * PriceHub 가격이 바뀌면 풀려 다시 검토. 옛 서버는 없음(= 보호 없음).
   */
  priceHeld?:        boolean;
}

/**
 * `NOCODE-{원상품번호}`는 판매자상품코드가 아예 없는 상품에 백엔드(ImportProductsJob)가
 * 붙이는 로컬 임시 코드다 — DB 유니크 제약상 빈 값을 못 넣어 원상품번호로 채운 것뿐,
 * 실제 스토어에는 코드가 없거나(판매금지 등 반영 불가) 이 임시값이 반영돼 있다.
 * 화면에는 실제 스토어와 맞춰 "코드 없음"으로 비워서 보여준다(2026-09-04).
 */
export const CODELESS_CODE_PREFIX = 'NOCODE-';

export const isCodelessPlaceholder = (sellerProductCode: string | null | undefined): boolean =>
  !!sellerProductCode && sellerProductCode.startsWith(CODELESS_CODE_PREFIX);

/** 화면 표시용 판매자상품코드 — 임시 placeholder면 빈 문자열. */
export const displaySellerCode = (sellerProductCode: string | null | undefined): string =>
  isCodelessPlaceholder(sellerProductCode) ? '' : (sellerProductCode ?? '');

export const DROP_SEVERITY_LABEL: Record<DropSeverity, string> = {
  SEVERE:   '심각',
  MODERATE: '중간',
  MINOR:    '경미',
};

/** 정렬 우선순위 — 낮을수록 먼저(심각 → 중간 → 경미). */
export const DROP_SEVERITY_RANK: Record<DropSeverity, number> = {
  SEVERE: 0, MODERATE: 1, MINOR: 2,
};

export const DROP_BUCKET_LABEL: Record<DropBucket, string> = {
  IN_RANGE:     '시세 범위 내',
  SUSPECT:      '확인 필요',
  NO_REFERENCE: '참조 시세 없음',
};

/** PriceHub 판매처 목록(price-snapshot) 항목 하나 — 하락 확인 리뷰에서 근거로 보여줌. */
export interface MarketItem {
  mallName: string | null;
  title:    string | null;
  price:    number | null;
  link:     string | null;
  image:    string | null;
}

export interface MarketSnapshot {
  items:       MarketItem[];
  minPrice:    number | null;
  maxPrice:    number | null;
  avgPrice:    number | null;
  collectedAt: string | null;
}

export const CARD_TYPE_LABEL: Record<CardType, string> = {
  POKEMON:       '포켓몬',
  ONE_PIECE:     '원피스',
  DIGIMON:       '디지몬',
  HOLOLIVE:      '홀로라이브',
  WEISS_SCHWARZ: '바이스슈발츠',
  COOKIERUN:     '쿠키런',
  OTHER:         '기타',
};

/**
 * 종류 필터 드롭다운(전체 + 종류별) 공용 옵션. 옛 가격 필터 바(PR #89 에서 지움)와 StockToolbar가 CARD_TYPE_LABEL을
 * 각자 다르게(하나는 일부만, 하나는 손으로 다시 타이핑) 옮겨 쓰면서 카드 목록 필터에만
 * 디지몬/기타가 빠져 있던 걸 여기서 하나로 합쳐 막는다.
 */
export const CARD_TYPE_FILTER_OPTIONS: { value: CardType | 'ALL'; label: string }[] = [
  { value: 'ALL', label: '전체' },
  ...(Object.keys(CARD_TYPE_LABEL) as CardType[]).map((t) => ({ value: t, label: CARD_TYPE_LABEL[t] })),
];